"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrcadDsnTransformPortGenerator = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const TransformSchematic_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformSchematic");
const TransformNetLabel_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformNetLabel");
const OrcadDsnSymbolUtils_1 = require("./OrcadDsnSymbolUtils");
const OrcadDsnBusUtils_1 = require("./OrcadDsnBusUtils");
const portOccurrenceNormalization_1 = require("../../../portOccurrenceNormalization");
const orcadIntersheetReferences_1 = require("../../../orcadIntersheetReferences");
const OrcadDsnPortOccurrenceUtils_1 = require("./OrcadDsnPortOccurrenceUtils");
const TransformProcessingResult_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProcessingResult");
class OrcadDsnTransformPortGenerator {
    constructor() {
        this.runsFor = [TransformSchematic_1.TransformSchematic];
    }
    *generate(parent, rootContext) {
        const page = parent.hierarchyEntry.page;
        const design = parent.hierarchyEntry.design;
        const reportOccurrenceDiagnostic = (message) => {
            parent.context.hierarchyPortDiagnostics = parent.context.hierarchyPortDiagnostics ?? new Set();
            parent.context.hierarchyPortDiagnostics.add(message);
        };
        for (const port of page.Port ?? []) {
            const symbolName = port.SymbolName ?? port.Name;
            const variantResolution = (0, OrcadDsnSymbolUtils_1.resolveSymbolVariant)(design.Cache?.PortSymbol, symbolName, port.LibName);
            if (variantResolution.ambiguous) {
                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                    type: 'symbol-variant-ambiguous',
                    message: `[symbol-variant-ambiguous] port symbol="${symbolName}" library="${port.LibName ?? ''}" does not select exactly one sourced cache entry; withheld`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                });
                continue;
            }
            const pinPlacement = (0, OrcadDsnSymbolUtils_1.computePortPinPosition)(design, port);
            const pinPosition = pinPlacement.position;
            if (!pinPlacement.pinRecorded) {
                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                    type: 'orcad-dsn-port-pin-unrecorded',
                    message: `[port-pin-unrecorded] port="${port.Name}" symbol="${symbolName}" has no cache pin record, so its connection point rests on the port's own origin`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                });
            }
            const declaredMembers = (0, portOccurrenceNormalization_1.expandBusPortName)(port.Name);
            const occurrenceMembers = (0, OrcadDsnPortOccurrenceUtils_1.childPortMemberOccurrenceBindings)(parent.instance, port.Name, reportOccurrenceDiagnostic);
            const members = declaredMembers.length > 0 && declaredMembers.length === occurrenceMembers.length ? declaredMembers
                : occurrenceMembers.length > 0 ?
                    occurrenceMembers.map((binding, declarationOrdinal) => ({
                        name: binding.memberName,
                        index: binding.identity.literalBitIndex ?? declarationOrdinal,
                    }))
                    : declaredMembers;
            const portOccurrence = (0, OrcadDsnPortOccurrenceUtils_1.childPortOccurrenceBindings)(parent.instance, port.Name, reportOccurrenceDiagnostic)[0];
            const hierarchyPortName = portOccurrence?.memberName ?? port.Name;
            const portStrategyMetadata = {
                'hierarchical-port': {
                    portDbId: port.DbId.toString(),
                    isPowerPort: port.Type === 7,
                },
                ...(portOccurrence ? { 'port-occurrence': portOccurrence.identity } : {}),
            };
            const netLabel = new TransformNetLabel_1.TransformNetLabel(parent, {
                position: pinPosition,
                originalLabel: hierarchyPortName,
                labelType: 'hierarchical-port',
                dimensions: members.length ? ['bus'] : undefined,
                strategyMetadata: portStrategyMetadata,
            });
            yield netLabel;
            rootContext.portNetLabelRecords = rootContext.portNetLabelRecords ?? [];
            rootContext.portNetLabelRecords.push({ page, portDbId: port.DbId, label: netLabel });
            if (members.length) {
                for (let memberOrdinal = 0; memberOrdinal < members.length; memberOrdinal++) {
                    const busMember = members[memberOrdinal];
                    const occurrenceIdentity = occurrenceMembers.find((binding) => binding.identity.declarationOrdinal === memberOrdinal)?.identity ??
                        (0, OrcadDsnPortOccurrenceUtils_1.childPortOccurrenceBindings)(parent.instance, busMember.name, reportOccurrenceDiagnostic)[0]?.identity;
                    const dimensionName = (0, OrcadDsnBusUtils_1.busDimensionName)(busMember);
                    const strategyMetadata = {
                        'hierarchical-port': {
                            portDbId: port.DbId.toString(),
                        },
                        'bus-member': {
                            busName: port.Name,
                            memberName: busMember.name,
                            memberIndex: busMember.index,
                        },
                        ...(occurrenceIdentity ? { 'port-occurrence': occurrenceIdentity } : {}),
                    };
                    yield new TransformNetLabel_1.TransformNetLabel(parent, {
                        position: pinPosition,
                        originalLabel: busMember.name,
                        labelType: 'hierarchical-port',
                        dimensions: [dimensionName],
                        strategyMetadata,
                    });
                }
            }
            const symbolDef = (0, OrcadDsnSymbolUtils_1.findPortSymbol)(design, port);
            if (symbolDef) {
                const { drawing, imageExtractions } = (0, OrcadDsnSymbolUtils_1.generateSymbolGroupDrawing)({
                    parent,
                    symbolDef,
                    boundingBox: symbolDef.BoundingBox,
                    locX: port.LocX,
                    locY: port.LocY,
                    rotation: port.Rotation ?? 0,
                    mirror: port.Mirror ?? 0,
                    name: port.Name,
                    displayProps: port.PortDisplayProp,
                    userProps: port.PortUserProp,
                    netRefObject: netLabel,
                    crossReferenceSource: { kind: orcadIntersheetReferences_1.OrCadIntersheetObjectKind.Port, dbId: port.DbId },
                });
                yield drawing;
                yield* imageExtractions;
            }
        }
    }
}
exports.OrcadDsnTransformPortGenerator = OrcadDsnTransformPortGenerator;
//# sourceMappingURL=OrcadDsnTransformPortGenerator.js.map